import React, { type ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { IconEthereumDark } from '@pkg/assets/icons/currency';
import {
    CurrencyDisplay,
    type CurrencyDisplayProps,
    PriceAction,
    type PriceActionProps,
    ThumbnailStack,
    type ThumbnailStackProps,
} from '@pkg/components';
import { tokens as thumbnailStackTokens } from '@pkg/components/ThumbnailStack/tokens';
import { FloorGameCrypto, FloorGameTierLabel, type NftCommonLibrary } from '@pkg/floorGame';

import { Label } from '../Label';
import { Ornament } from '../Ornament';

import { TierStats } from './components';

import {
    Column,
    Columns,
    CurrencyBackground,
    CurrencyLogo,
    Interior,
    LabelWrapper,
    MaxTier,
    MaxTierEmoji,
    OrnamentWrapper,
    StyledPriceGame,
    ThumbnailStackWrapper,
} from './styles';

export interface PriceGameProps {
    label?: string;
    currency?: CurrencyDisplayProps['currency'];
    price?: CurrencyDisplayProps['price'];
    localConversion?: CurrencyDisplayProps['localConversion'];
    tier?: FloorGameTierLabel;
    reviveAction?: PriceActionProps['url'] | PriceActionProps['onAction'];
    // In the future, `nextNfts` might have to be a complete
    // timeline of collection activity. We would then be able to
    // render `<TicksMeter />` with that same data.
    nextNfts?: NftCommonLibrary;
    // It was expected that we would get the "NFTs within tier" count
    // from `nextNfts.length`, but that array might only contain the
    // NFTs needed to render <TicksMeter />.
    listingsCount?: number;
    compact?: boolean;
    // TODO: This is temporary until we can improve the
    // Pod's page grid to better control Card widths.
    showThumbnails?: boolean;
    // TODO: This is currently being used for `<TicksMeter />`,
    // but we should replace this with a dedicated API.
    children?: ReactNode;
    // TODO: Consider an `animated` prop to optionally
    // apply the framer-motion props.
    portable?: boolean;
}

const maxThumbnails = thumbnailStackTokens.list.maxItems + 1;

export function PriceGame({
    label,
    currency,
    price = 0,
    localConversion,
    tier = FloorGameTierLabel.Tier1,
    reviveAction,
    nextNfts = [],
    listingsCount = 0,
    compact = false,
    showThumbnails = false,
    portable = false,
    children,
}: PriceGameProps) {
    const { t: translate } = useTranslation('floorgame');

    // TODO: This will need to be expanded to support
    // additional currency logos.
    const currencyLogo =
        currency === FloorGameCrypto.Ethereum && !portable ? (
            <CurrencyLogo>
                <IconEthereumDark />
            </CurrencyLogo>
        ) : null;

    const currencyBackground = currencyLogo ? <CurrencyBackground>{currencyLogo}</CurrencyBackground> : null;

    const ornamentMarkup =
        tier === FloorGameTierLabel.Tier1 || compact ? null : (
            <OrnamentWrapper key={`PriceGame-Ornament-${tier}`}>
                <Ornament tier={tier} />
            </OrnamentWrapper>
        );

    const priceLabelMarkup = label ? (
        <LabelWrapper>
            <Label key="PriceGame-Label" content={label} />
        </LabelWrapper>
    ) : null;

    const cheapestAvailableNft = nextNfts[0];
    const reviveActionMarkup =
        reviveAction && cheapestAvailableNft && tier !== FloorGameTierLabel.Tier5 ? (
            <PriceAction
                currency={currency}
                compact={compact}
                label={translate('price_game_revive_cta')}
                ariaLabel={translate('price_game_revive_a11y')}
                url={typeof reviveAction === 'string' ? reviveAction : undefined}
                external={typeof reviveAction === 'string'}
                onAction={typeof reviveAction === 'function' ? reviveAction : undefined}
            />
        ) : null;

    const tierStatsMarkup = reviveActionMarkup ? null : (
        <TierStats remaining={listingsCount || nextNfts.length} compact={compact} />
    );

    const canRenderSuffix = showThumbnails && !compact && !reviveActionMarkup;
    const canRenderThumbnails = Boolean(
        canRenderSuffix && (listingsCount || nextNfts.length) && tier !== FloorGameTierLabel.Tier5,
    );

    // TODO: We may want to convert `thumbnails` to `state`, so that we can
    // use a `useEffect` to iterate over `nextNfts` and compare the first 4 `ids`
    // to the currently help `state` array. Only update `state` if the `ids` do not
    // match. This way, we avoid triggering re-renders on identical arrays.
    const thumbnails = canRenderThumbnails ? getThumbnailsFromNfts(nextNfts) : [];

    // We may want to render this even when `nextNfts`
    // is empty as a "skeleton loader"... but then again,
    // passing an empty `[]` is a nice way to selectively
    // omit the <ThumbnailStack />.
    const thumbnailStackMarkup = thumbnails.length ? (
        <ThumbnailStackWrapper>
            <ThumbnailStack items={thumbnails} animateExit />
        </ThumbnailStackWrapper>
    ) : null;

    const maxTierMarkup =
        canRenderSuffix && tier === FloorGameTierLabel.Tier5 ? (
            <MaxTier>
                <MaxTierEmoji>🎉</MaxTierEmoji>
            </MaxTier>
        ) : null;

    return (
        <StyledPriceGame $compact={compact}>
            {currencyBackground}
            {ornamentMarkup}
            {priceLabelMarkup}

            <Interior $labelSpacing={Boolean(!compact && priceLabelMarkup)}>
                <Columns>
                    <Column $currencyOffset>
                        <CurrencyDisplay
                            symbol
                            currency={currency}
                            price={price}
                            label={localConversion ? translate('price_game_label') : undefined}
                            localConversion={localConversion}
                            compact={compact}
                        />
                    </Column>

                    <Column>
                        {tierStatsMarkup}
                        {reviveActionMarkup}
                        {thumbnailStackMarkup}
                        {maxTierMarkup}
                    </Column>
                </Columns>

                {children}
            </Interior>
        </StyledPriceGame>
    );
}

function getThumbnailsFromNfts(nfts: NftCommonLibrary): NonNullable<ThumbnailStackProps['items']> {
    // We are slicing thumbnails before passing to <ThumbnailStack />
    // because we don't want to trigger re-renders on "under-stack" NFTs.
    const curatedThumbnails = nfts.slice(0, maxThumbnails);

    return curatedThumbnails.map(({ id, asset }) => ({
        id,
        videoUrl: asset.videoUrl,
        imageUrl: asset.imageUrl,
        title: asset.title,
    }));
}
